import React, { useEffect } from "react";
import { Card } from "../common";
import {
  useAppDispatch,
  useAppSelector,
  getActiveAnnouncementsThunk,
} from "../../redux";
import micIcon from "../../assets/icons/mic-purple.svg";
import calendarIcon from "../../assets/icons/calendar.svg";
import "./AnnouncementCard.css";

interface AnnouncementCardProps {
  title?: string;
  className?: string;
}

const AnnouncementCard: React.FC<AnnouncementCardProps> = ({
  title = "Announcements",
  className = "",
}) => {
  const dispatch = useAppDispatch();
  const { activeRecords, isLoading, error } = useAppSelector(
    (state) => state.announcement,
  );

  useEffect(() => {
    dispatch(getActiveAnnouncementsThunk());
  }, [dispatch]);

  const formatDateRange = (
    startDate: string | null,
    endDate: string | null,
  ) => {
    if (!startDate && !endDate) return null;
    if (startDate && endDate) {
      if (startDate === endDate) return startDate;
      return `${startDate} to ${endDate}`;
    }
    return startDate || endDate;
  };

  return (
    <Card className={`announcement-card-container ${className}`}>
      <div className="announcement-card-header">
        <div className="announcement-card-title-group">
          <div className="announcement-card-icon-wrapper">
            <img src={micIcon} alt="Announcements" width={20} height={20} />
          </div>
          <h3 className="announcement-card-title">{title}</h3>
          {activeRecords.length > 0 && (
            <span className="announcement-card-badge">
              {activeRecords.length}
            </span>
          )}
        </div>
      </div>

      <div className="announcement-card-content">
        {isLoading && activeRecords.length === 0 ? (
          <div className="announcement-card-loading">
            <div className="announcement-card-spinner" />
            <span>Loading announcements...</span>
          </div>
        ) : error && activeRecords.length === 0 ? (
          <div className="announcement-card-empty">
            <p className="announcement-empty-title">Unable to load announcements</p>
            <p className="announcement-empty-sub">{error}</p>
          </div>
        ) : activeRecords.length === 0 ? (
          <div className="announcement-card-empty">
            <p className="announcement-empty-title">
              No announcements active right now.
            </p>
            <p className="announcement-empty-sub">
              Check back later for updates from your teachers.
            </p>
          </div>
        ) : (
          <div className="announcement-card-list">
            {activeRecords.map((item) => {
              const dateRange = formatDateRange(
                item.Start_Date,
                item.End_Date,
              );
              const teacherName = item["Teacher.Name"];

              return (
                <div key={item.id} className="announcement-card-item">
                  <div className="announcement-card-item-top">
                    <h4 className="announcement-card-item-title">
                      {item.Name}
                    </h4>
                    {teacherName && (
                      <span className="announcement-card-teacher-badge">
                        {teacherName}
                      </span>
                    )}
                  </div>

                  {dateRange && (
                    <div className="announcement-card-item-date">
                      <img
                        src={calendarIcon}
                        alt="Date"
                        width={14}
                        height={14}
                      />
                      <span>{dateRange}</span>
                    </div>
                  )}

                  {item.Description && (
                    <p className="announcement-card-item-desc">
                      {item.Description}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Card>
  );
};

export default AnnouncementCard;
